import ErrorLogger from '../services/ErrorLogger';

class GlobalErrorHandler {
  private static instance: GlobalErrorHandler;

  static getInstance(): GlobalErrorHandler {
    if (!GlobalErrorHandler.instance) {
      GlobalErrorHandler.instance = new GlobalErrorHandler();
    }
    return GlobalErrorHandler.instance;
  }

  init() {
    // Only run in browser environment
    if (typeof window === 'undefined') {
      console.log('Error tracking disabled in server environment');
      return;
    }

    // Disable error tracking in production to reduce noise
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
      return; // Skip error tracking setup in production
    }
    console.log('Error tracking enabled in development');
    this.setupFetchInterceptor();
    this.setupWindowErrorHandlers();
    this.setupUnhandledRejectionHandler();
  }

  private setupFetchInterceptor() {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        if (!response.ok) {
          this.handleFetchError(args[0], response);
        }
        return response;
      } catch (error) {
        this.handleFetchError(args[0], null, error as Error);
        throw error;
      }
    };
  }

  private setupWindowErrorHandlers() {
    // JavaScript errors
    window.addEventListener('error', (event) => {
      ErrorLogger.logError(event.error || event.message, {
        request: {
          url: window.location.href,
          method: 'GET',
          headers: {},
          body: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno
          }
        }
      });
    });

    // Resource loading errors
    window.addEventListener('error', (event) => {
      if (event.target !== window) {
        const target = event.target as HTMLElement;
        ErrorLogger.logError(`Resource loading failed: ${target.tagName}`, {
          request: {
            url: window.location.href,
            method: 'GET',
            headers: {},
            body: {
              resourceType: target.tagName,
              resourceSrc: target.getAttribute('src') || target.getAttribute('href')
            }
          }
        });
      }
    }, true);
  }

  private setupUnhandledRejectionHandler() {
    window.addEventListener('unhandledrejection', (event) => {
      ErrorLogger.logError(event.reason || 'Unhandled Promise Rejection', {
        request: {
          url: window.location.href,
          method: 'GET',
          headers: {},
          body: {
            promiseRejection: true,
            reason: event.reason
          }
        }
      });
    });
  }

  private handleFetchError(input: RequestInfo | URL, response: Response | null, error?: Error) {
    // Prevent logging errors for error logging endpoints to avoid loops
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : 'unknown';
    if (url.includes('/api/errors') || url.includes('/api/logs') || url.includes('/api/audit-logs')) {
      return;
    }
    
    const user = this.getCurrentUser();
    
    try {
      // Add additional loop prevention by checking if this is an error logging request
      const isLoggingRequest = url.includes('/api/errors') ||
                              (typeof input === 'object' && input.url && input.url.includes('/api/errors'));
      
      if (isLoggingRequest) {
        console.log('[ERROR HANDLER] Skipping error logging for error logging request to prevent loops');
        return;
      }
      
      ErrorLogger.logError(error?.message || 'API Request Failed', {
        user,
        request: {
          url,
          method: 'unknown',
          headers: {},
          body: null
        },
        response: {
          status: response?.status || 0,
          data: null
        }
      });
    } catch (logError) {
      // Prevent error loops - just log to console
      console.error('Failed to log error:', logError);
    }
  }

  private getCurrentUser() {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return { id: user.id, email: user.email };
      }
    } catch (_e) {
      // Ignore parsing errors
    }
    return undefined;
  }

  // Manual error reporting method
  reportError(error: Error | string, context?: Record<string, unknown>) {
    const user = this.getCurrentUser();

    ErrorLogger.logError(error, {
      user,
      request: {
        url: window.location.href,
        method: 'GET',
        headers: {},
        body: context
      }
    });
  }
}

export default GlobalErrorHandler.getInstance();